import { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageTransition } from './motion';
import {
  LayoutDashboard,
  Building,
  Users,
  Receipt,
  Zap,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Home,
  CalendarDays,
  Wallet,
  Upload,
  Shield,
  UserCog,
  Settings,
  IndianRupee,
  CalendarCheck,
  HelpCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
  { path: '/properties', icon: Building, label: 'Properties', module: 'properties' },
  { path: '/rooms', icon: Home, label: 'Rooms', module: 'rooms' },
  { path: '/guests', icon: Users, label: 'Guests', module: 'guests' },
  { path: '/bookings', icon: CalendarCheck, label: 'Bookings', module: 'bookings' },
  { path: '/electricity', icon: Zap, label: 'Electricity', module: 'electricity' },
  { path: '/receipts', icon: IndianRupee, label: 'Receipts', module: 'receipts' },
  { path: '/expenses', icon: Wallet, label: 'Payments', module: 'payments' },
  { path: '/daily-hisab', icon: CalendarDays, label: 'Daily Hisab', module: 'daily_hisab' },
  { path: '/reports', icon: FileText, label: 'Reports', module: 'reports' },
  { path: '/organisation-settings', icon: Settings, label: 'Settings', module: 'properties' },
];

const adminNavItems = [
  { path: '/import', icon: Upload, label: 'Import Data', module: 'import' },
  { path: '/users', icon: UserCog, label: 'User Management', module: 'users' },
  { path: '/help', icon: HelpCircle, label: 'Help & Legal', module: null },
];

const ROLE_COLORS = {
  super_admin: 'bg-purple-600',
  admin: 'bg-red-500',
  manager: 'bg-blue-500',
  accountant: 'bg-green-500',
  viewer: 'bg-gray-500'
};

export const Layout = ({ children }) => {
  const { user, logout, hasModuleAccess, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter nav items based on user's module access
  const filteredNavItems = navItems.filter(item => hasModuleAccess(item.module));
  
  // Filter admin nav items based on user's module access
  const filteredAdminNavItems = adminNavItems.filter(item => {
    if (item.module === null) return true; // Help & Legal always visible
    return hasModuleAccess(item.module);
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile menu button */}
      <div className="fixed top-4 left-4 z-50 lg:hidden">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-slate-900 text-white z-40 transition-all duration-300 flex flex-col
          ${sidebarOpen ? 'w-64' : 'w-20'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
          {sidebarOpen && (
            <Link to="/dashboard" className="group" title="Go to Dashboard" data-testid="sidebar-logo-link">
              <h1 className="text-lg font-bold tracking-tight group-hover:text-blue-400 transition-colors">Stay Point</h1>
              {user?.organisation_name && (
                <p className="text-[10px] text-blue-400 truncate">{user.organisation_name}</p>
              )}
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={`h-5 w-5 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-2 flex-1 overflow-y-auto" data-testid="sidebar-nav">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mb-1 rounded-lg transition-colors
                ${isActive 
                  ? 'bg-blue-800 text-white' 
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }
                ${!sidebarOpen ? 'justify-center' : ''}
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}

          {/* Admin Section */}
          {filteredAdminNavItems.length > 0 && (
            <>
              {sidebarOpen && (
                <div className="mt-6 mb-2 px-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <Shield className="h-3 w-3" />
                    Admin
                  </p>
                </div>
              )}
              {filteredAdminNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 mb-1 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-blue-800 text-white' 
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                    ${!sidebarOpen ? 'justify-center' : ''}
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {sidebarOpen && <span>{item.label}</span>}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-800">
          <div className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
            {sidebarOpen && (
              <div className="truncate">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <Badge className={`${ROLE_COLORS[user?.role] || 'bg-gray-500'} text-white text-[10px] px-1.5 py-0`}>
                    {user?.role?.toUpperCase()}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Company Branding */}
        {sidebarOpen && (
          <div className="px-4 py-3 border-t border-slate-800 text-center">
            <p className="text-[10px] text-slate-500">Created & Managed by</p>
            <p className="text-xs text-slate-400 font-medium">Stay Point Private Limited</p>
            <p className="text-[9px] text-slate-600 mt-1">All Rights Reserved</p>
          </div>
        )}
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <main
        className={`min-h-screen transition-all duration-300 ${
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        }`}
        data-testid="main-content"
      >
        <div className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8 pb-20">
          <PageTransition>{children}</PageTransition>
        </div>
        
        {/* Footer */}
        <footer className="fixed bottom-0 right-0 left-0 lg:left-64 bg-white border-t border-slate-200 py-3 px-6 text-center z-10 transition-all duration-300" style={{ marginLeft: sidebarOpen ? '' : '5rem' }}>
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} <span className="font-semibold text-slate-700">Stay Point Private Limited</span> • All Rights Reserved
          </p>
        </footer>
      </main>
    </div>
  );
};
